import React, { startTransition, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ProfileOrders from "./ProfileOrders";
import AllOrdersSeller from "../../components/Shop/AllOrders";
import AllTransactions from "../../components/Profile/AllTransactions";
import { useUnreadNotificationCount } from "../../hooks/useUnreadNotificationCount";
import {
  SELLER_REFUND_REVIEW_TYPES,
  BUYER_REFUND_UPDATE_TYPES,
  ORDER_NOTIFICATION_TYPES,
} from "../../utils/notificationTypes";
import {
  isBuyerFacingOrderNotification,
  isSellerFacingOrderNotification,
} from "../../utils/orderNotificationMatch";

const TabBadge = ({ count }) =>
  count > 0 ? (
    <span className="ml-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const ProfileOrdersUnified = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.user);

  const buyerFacingPredicate = useCallback(
    (n) => isBuyerFacingOrderNotification(n),
    []
  );
  const sellerFacingPredicate = useCallback(
    (n) => isSellerFacingOrderNotification(n),
    []
  );

  const sellerRefundUnread = useUnreadNotificationCount(SELLER_REFUND_REVIEW_TYPES);
  const sellerOrderUnread = useUnreadNotificationCount(
    ORDER_NOTIFICATION_TYPES,
    sellerFacingPredicate
  );
  const buyerRefundUnread = useUnreadNotificationCount(
    BUYER_REFUND_UPDATE_TYPES,
    buyerFacingPredicate
  );
  const buyingOrderUnread = useUnreadNotificationCount(
    ORDER_NOTIFICATION_TYPES,
    buyerFacingPredicate
  );

  const isSeller = Boolean(user?.isSeller);
  const sellerTabUnread = sellerRefundUnread + sellerOrderUnread;
  const buyingTabUnread = buyingOrderUnread + buyerRefundUnread;

  // Single source of truth from URL (primitive deps to avoid render loops)
  const viewParam = searchParams.get("view") || searchParams.get("tab") || "buying";
  const viewFromUrl =
    viewParam === "selling" || viewParam === "buying" || viewParam === "transactions"
      ? viewParam
      : "buying";

  const effectiveMode = viewFromUrl === "selling" && !isSeller ? "buying" : viewFromUrl;

  const showSellerRefundBanner = useMemo(
    () => isSeller && sellerRefundUnread > 0,
    [isSeller, sellerRefundUnread]
  );

  const handleChangeMode = (nextMode) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("view", nextMode);
    startTransition(() => {
      setSearchParams(newParams, { replace: true });
    });
  };

  return (
    <div className="space-y-4">
      <header className="mb-2">
        <h2 className="text-lg font-semibold text-slate-900">Orders</h2>
        <p className="text-sm text-slate-500">
          View your purchases and orders from your customers.
        </p>
      </header>

      <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-sm font-medium">
        <button
          type="button"
          onClick={() => handleChangeMode("buying")}
          className={`px-4 py-1 rounded-full transition inline-flex items-center ${
            effectiveMode === "buying"
              ? "bg-white text-slate-900 shadow"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Buying
          <TabBadge count={buyingTabUnread} />
        </button>
        {isSeller && (
          <button
            type="button"
            onClick={() => handleChangeMode("selling")}
            className={`px-4 py-1 rounded-full transition inline-flex items-center ${
              effectiveMode === "selling"
                ? "bg-white text-slate-900 shadow"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Selling
            <TabBadge count={sellerTabUnread} />
          </button>
        )}
        <button
          type="button"
          onClick={() => handleChangeMode("transactions")}
          className={`px-4 py-1 rounded-full transition ${
            effectiveMode === "transactions"
              ? "bg-white text-slate-900 shadow"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Transactions
        </button>
      </div>

      {showSellerRefundBanner && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          role="status"
        >
          <p className="font-semibold">
            {sellerRefundUnread === 1
              ? "1 refund request needs your review"
              : `${sellerRefundUnread} refund requests need your review`}
          </p>
          <p className="mt-1 text-amber-900/90">
            Open the Selling tab to see flagged orders, or go straight to Buyer Refunds.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {effectiveMode !== "selling" ? (
              <button
                type="button"
                onClick={() => handleChangeMode("selling")}
                className="rounded-lg bg-[#38513b] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2f4232]"
              >
                View selling orders
              </button>
            ) : null}
            <Link
              to="/profile/disputes-refunds?tab=review-refunds"
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100"
            >
              Buyer refunds
            </Link>
          </div>
        </div>
      )}

      {effectiveMode === "buying" ? (
        <ProfileOrders />
      ) : effectiveMode === "transactions" ? (
        <AllTransactions />
      ) : (
        <AllOrdersSeller />
      )}
    </div>
  );
};

export default ProfileOrdersUnified;
